import { useEffect, useState } from "react";

import displayToast from "@common/components/Toast/displayToast";
import useScoringConfig, { ScoringConfigKeys } from "@main/pages/Release/consumers/useScoringConfig";
import { ThresholdRowModel } from "@main/pages/Release/views/MetricDetails/shared/MetricRow";

export default function useCrashFreeSessionsScoring(refetchReleaseStatus: () => void) {
  const { scoringConfig, scoringConfigLoading, scoringConfigError, saveScoringConfig, isSaving } = useScoringConfig(
    ScoringConfigKeys.crash_free_sessions,
  );

  const [thresholds, setThresholds] = useState<number[]>(() => THRESHOLDS.map(() => 0));

  useEffect(() => {
    const anchors = scoringConfig?.config.anchors;
    if (!anchors?.length) return;

    setThresholds(
      THRESHOLDS.map((t) => {
        const match = anchors.find((anchor) => anchor[1] === t.score);
        return match ? match[0] : 0;
      }),
    );
  }, [scoringConfig]);

  const handleThresholdChange = (index: number, value: number) => {
    setThresholds((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  };

  const handleSave = () => {
    saveScoringConfig(
      {
        anchors: THRESHOLDS.map((def, i) => [thresholds[i], def.score]).reverse(),
      },
      {
        onSuccess: () => {
          refetchReleaseStatus();
          displayToast({ title: "Saved successfully", content: "Your scoring configuration is saved." });
        },
        onError: () => {
          displayToast({ title: "Something went wrong", content: "Please try again." });
        },
      },
    );
  };

  const thresholdRows: ThresholdRowModel[] = THRESHOLDS.map((def, i) => ({
    score: def.score,
    label: def.label,
    value: thresholds[i],
    error: getValidationError(i, thresholds),
    onChange: (v: number) => handleThresholdChange(i, v),
    description: getDescription(def),
  }));

  return {
    thresholdRows,
    scoringConfigLoading,
    scoringConfigError,
    handleSave,
    isSaving,
  };
}

interface ThresholdConfig {
  score: number;
  default: number;
  label: string;
  range?: string;
  isFirst?: boolean;
  isLast?: boolean;
}

const THRESHOLDS: ThresholdConfig[] = [
  { score: 8, default: 99.5, label: "excellent", range: "8.0 up to 10.0" },
  { score: 6, default: 99, label: "good", range: "6.0 up to 7.9" },
  { score: 4, default: 98, label: "warning", range: "4.0 up to 5.9" },
  { score: 2, default: 95, label: "concerning", range: "2.0 up to 3.9" },
  { score: 0, default: 90, label: "critical", range: "0.0 up to 1.9", isLast: true },
];

const formatScoreLabel = (score: number): string => (score === 10 ? "10" : `${score}.0`);

const getDescription = (def: ThresholdConfig): string => {
  if (def.isFirst) {
    return `You consider this  and above as '${def.label}' (score ${
      def.range
    }). Score reaches 10.0 at 100%. ← System default: ${def.default.toString()}%.`;
  }
  if (def.isLast) {
    return `You consider this percentage, and everything below, as a '${
      def.label
    }' 0.0 score. ← System default: ${def.default.toString()}%.`;
  }
  return `You consider this percentage as the lowest threshold for a '${def.label}' score (${
    def.range
  }). ← System default: ${def.default.toString()}%.`;
};

const getValidationError = (index: number, values: number[]): string | undefined => {
  const value = values[index];
  const prevValue = index > 0 ? values[index - 1] : undefined;
  const nextValue = index < values.length - 1 ? values[index + 1] : undefined;

  const tooSmall = prevValue !== undefined && value >= prevValue;
  const tooBig = nextValue !== undefined && value <= nextValue;

  if (!tooSmall && !tooBig) return undefined;

  if (nextValue === undefined) {
    const prevScore = THRESHOLDS[index - 1].score;
    return `Must be greater than ${prevValue}% (${formatScoreLabel(prevScore)} score threshold).`;
  }
  if (prevValue === undefined) {
    const nextScore = THRESHOLDS[index + 1].score;
    return `Must be less than ${nextValue}% (${formatScoreLabel(nextScore)} score threshold).`;
  }

  const prevScore = THRESHOLDS[index - 1].score;
  const nextScore = THRESHOLDS[index + 1].score;
  return `Must be between ${nextValue}% (${formatScoreLabel(
    nextScore,
  )} score threshold) and ${prevValue}% (${formatScoreLabel(prevScore)} score threshold).`;
};
